<template>
  <section class="user-select-none">
    <b-button v-b-toggle="'collar-size-select-help'" variant="link" class="float-right mb-0 text-primary p-0">
      <h3>
        <u>{{ $t('otherProductSelect.sizingHelp') }}</u>
      </h3>
    </b-button>
    <h2 class="mb-0">{{ $t('productSizeSelect.title') }}</h2>
    <h3 class="d-none d-xl-block text-dark">{{ $t('productSizeSelect.approxSize') }}</h3>
    <b-collapse id="collar-size-select-help" class="mt-3 px-4 py-3">
      <div class="arrow" />
      <div class="position-relative float-right">
        <h4 v-b-toggle="'collar-size-select-help'" class="text-primary" href="javascript:void(0)">
          <u>Close</u>
        </h4>
      </div>
      <div class="text-center" style="margin-left: 49px">
        <h3 class="font-weight-bold text-dark">Sizing Help</h3>
      </div>
      <h4 class="text-justify text-dark mx-n1">
        Follow these tips to pick the perfect size for your furry friend: Using a soft tape measure, pull the tape snugly around your pet's
        neck. Make sure you're able to slip two fingers between the "collar" and your pup's neck to ensure you're not measuring too tightly.
        Don't have a tape measure? Use a piece of string and then measure the string with a ruler.
      </h4>
      <b-img
        class="collar-size-select-help-img"
        width="100%"
        src="https://us.docupet.com/media/uploads/public/system/63/17/6317ce81ac4810b9c626665d2b2b03d7514d5d913b6a14cb353544d522eb0361.gif"
        center
        fluid
        fluid-grow
        alt="Measure Collar Size GIF"
      />
    </b-collapse>
    <b-row class="my-3">
      <b-col v-for="[sizeKey, sizeDetails] in Object.entries(sizes)" :key="sizeKey" class="text-center">
        <b-button
          :id="`collar-size-select-${sizeKey}`"
          :ref="sizeKey"
          class="size-option"
          block
          :class="{ 'no-stock': isOutOfStock(sizeDetails), selected: size === sizeKey }"
          :variant="!isOutOfStock(sizeDetails) ? 'outline-primary' : 'outline'"
          :disabled="isOutOfStock(sizeDetails)"
          :aria-label="`Choose ${sizeKey}`"
          @focus="setFocus(sizeKey)"
          @focusout="unsetFocus"
          @click="isOutOfStock(sizeDetails) ? null : setSize(sizeKey)"
        >
          <p class="h3 mb-0" :class="isOutOfStock(sizeDetails) ? 'text-light' : 'text-primary'">
            <strong>{{ $t(`productSizeSelect.${sizeKey}.text`) }}</strong>
          </p>
          <svg v-show="isOutOfStock(sizeDetails)" aria-hidden="true"><line x1="0" y1="100%" x2="100%" y2="0" /></svg>
        </b-button>
        <div v-if="isOutOfStock(sizeDetails)" class="text-danger copy" v-text="$t('product.outOfStock')" />
        <b-popover
          v-if="!isOutOfStock(sizeDetails)"
          :target="`collar-size-select-${sizeKey}`"
          triggers="hover"
          placement="bottom"
          variant="none"
          custom-class="size-pop-over user-select-none d-none d-lg-block"
        >
          <div>
            <h2 v-if="sizeDetails.title" class="text-center text-dark font-weight-bold">{{ sizeDetails.title }}</h2>
            <h4 class="text-center text-dark mt-n1">
              <span v-if="sizeDetails.length"><strong>Length:</strong>&nbsp;{{ sizeDetails.length }}&nbsp;<strong>|</strong>&nbsp;</span>
              <span v-if="sizeDetails.width"><strong>Width:</strong>&nbsp;{{ sizeDetails.width }}</span>
            </h4>
            <b-row class="mt-3">
              <h4 v-if="sizeDetails.description" class="text-dark px-3">
                {{ sizeDetails.description }}
              </h4>
            </b-row>
          </div>
        </b-popover>
      </b-col>
      <b-collapse
        v-for="[sizeKey, sizeDetails] in selectedSize ? [selectedSize] : []"
        :id="`collar-size-select-info-${size}`"
        :key="`size-info-collapse-${sizeKey}`"
        class="d-block d-lg-none mt-3 p-2 mx-3 size-collapse"
        visible
      >
        <h4 v-if="isOutOfStock(sizeDetails)" class="text-center text-dark font-weight-bold py-2 my-auto">Temporarily out of stock</h4>
        <div v-else>
          <h2 v-if="sizeDetails.title" class="text-center text-dark font-weight-bold">{{ sizeDetails.title }}</h2>
          <h4 class="text-center text-dark mt-n1">
            <span v-if="sizeDetails.length"><strong>Length:</strong>&nbsp;{{ sizeDetails.length }}&nbsp;<strong>|</strong>&nbsp;</span>
            <span v-if="sizeDetails.width"><strong>Width:</strong>&nbsp;{{ sizeDetails.width }}</span>
          </h4>
          <b-row class="mt-3">
            <h4 v-if="sizeDetails.description" class="text-dark px-3">
              {{ sizeDetails.description }}
            </h4>
          </b-row>
        </div>
      </b-collapse>
    </b-row>
    <ValidationProvider v-slot="{ errors }" vid="p-size-select" :rules="{ required: { allowFalse: false } }" slim>
      <input id="product-style-color" v-model="value" type="text" class="d-none" hidden />
      <span v-if="errors && errors[0]" class="text-danger">{{ $t('productSizeSelect.error') }}</span>
    </ValidationProvider>
  </section>
</template>

<script>
import getImage from '@/mixins/getImage';
import { ProductMixins } from '@/mixins/components/product';
import { KeyboardNavigationMixins } from '@/mixins/keyboardNavigation';

export default {
  name: 'CollarSizeSelect',
  mixins: [getImage, ProductMixins, KeyboardNavigationMixins],
  computed: {
    selectedSize() {
      return Object.entries(this.sizes)?.find((e) => e?.[0] === this.size);
    },
    navigableOptions() {
      return Object.entries(this.sizes)
        .filter(([size, details]) => {
          if (details.inStock) {
            return size;
          }
        })
        .map(([size]) => size);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.size-option {
  overflow: hidden;
  border-radius: 10px;
  border: 2px solid $dp-gray-4;
  height: auto;
  padding-top: 0.5rem;
  &.selected {
    border: 2px solid $dp-blue;
    background-color: rgba(204, 236, 249, 0.5);
  }
  &:hover,
  &:active,
  &:focus {
    &:not(.disabled) {
      border: 2px solid $dp-blue;
      box-shadow: 0 0 0 0.2rem $dp-light-blue;
      background-color: rgba(204, 236, 249, 0.5) !important;
    }
  }
}
#collar-size-select-help {
  position: relative;
  border-radius: 6px;
  background-color: $dp-gray-1;
  border: 2px solid $dp-gray-3;
  &:not(.collapsing) .arrow {
    &:after,
    &:before {
      left: 94%;
      content: '';
      bottom: 100%;
      position: absolute;
      pointer-events: none;
      border: solid transparent;
    }
    &:after {
      border-color: rgba(247, 247, 247, 0);
      border-bottom-color: $dp-gray-1;
      border-width: 12px;
      margin-left: -12px;
    }
    &:before {
      border-color: rgba(220, 220, 220, 0);
      border-bottom-color: $dp-gray-3;
      border-width: 15px;
      margin-left: -15px;
    }
  }
}
.collar-size-select-help-img {
  @media (min-width: 991px) {
    width: 50% !important;
  }
}
.size-out-of-stock-pop-over {
  &.bs-popover-top {
    top: -10px !important;
  }
  &.bs-popover-bottom {
    top: 10px !important;
  }
  &.bs-popover-right {
    left: 10px !important;
  }
  &.bs-popover-left {
    left: -10px !important;
  }
  @media (min-width: 991px) {
    max-width: 600px;
  }
}
.size-pop-over {
  &.bs-popover-top {
    top: -10px !important;
  }
  &.bs-popover-bottom {
    top: 10px !important;
  }
  &.bs-popover-right {
    left: 10px !important;
  }
  &.bs-popover-left {
    left: -10px !important;
  }
  @media (min-width: 991px) {
    max-width: 600px;
  }
  background-color: $dp-gray-1;
}
.size-collapse {
  border-radius: 6px;
  background-color: $dp-gray-1;
  border: 2px solid $dp-gray-3;
}
</style>
